import { DataGrid } from '@mui/x-data-grid'
import translate from '../../../i18n/translate'
import { modalsSlice } from '../../../modules/store/reducers/modalsSlice'
import useStyles from './useStyles'
import { Button, IconButton, Typography } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { Box } from '@mui/system'
import { useEffect, useState } from 'react'
import toast, { Toaster } from 'react-hot-toast'
import { useAppDispatch } from '../../../modules/store/hooks'
import { organizerApi } from '../../../modules/services/organizerService'
import { format } from 'date-fns'
import TransactionActionModal from '../../Modals/TransactionActionModal'
import { eventApi } from '../../../modules/services/eventService'
import { useNavigate } from 'react-router-dom'
import download from '../../../common/download'
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

type Props = {
  type: string;
  organizerId: number;
  transactionId?: number;
}

const TAKE_COUNT = 10;

const OrganizerTransactionDataGrid = (props: Props) => {
  const { type, organizerId, transactionId } = props

  const classes = useStyles()
  const dispatch = useAppDispatch()
  const navigate = useNavigate();
  const [count, setCount] = useState<number>(0)
  const [columns, setColumns] = useState<any[]>([])
  const [rows, setRows] = useState<any[]>([])
  const [selectedTransactionId, setSelectedTransactionId] = useState<number | null>(null)
  const [modalMode, setModalMode] = useState<string | null>(null)

  //#region API requests
  const [approveTransaction] = eventApi.useApproveTransactionMutation()
  const [declineTransaction] = eventApi.useDeclineTransactionMutation()
  const [organizerTransactionListDataLazyFetch] = organizerApi.useLazyGetOrganizerTransactionListQuery()
  const {
    refetch: organizerTransactionListDataRefetch,
    data: organizerTransactionListData,
    isSuccess: isOrganizersSuccess,
    isFetching: isOrganizersFetching
  } = organizerApi.useGetOrganizerTransactionListQuery({ type, organizerId, transactionId }, { skip: !type && !organizerId })

  const { toggleTransactionActionModal } = modalsSlice.actions

  const handleOpenRejectTransactionModal = (id: number) => {
    dispatch(toggleTransactionActionModal());
    setModalMode('reject');
    setSelectedTransactionId(id)
  };

  const handleOpenApproveTransactionModal = (id: number) => {
    dispatch(toggleTransactionActionModal());
    setModalMode('approve');
    setSelectedTransactionId(id)
  };

  const showEventInfo = (id: number) => {
    navigate(`/events/all?eventId=${id}`)
  }

  const handleRejectTransaction = async (id: number, text?: string | null) => {
    try {
      const eventId = rows.find((r) => r.id === id)?.event?.id;

      if (eventId) {
        await declineTransaction({ transactionId: id, eventId, adminNote: text })
        
        setRows((prev) => {
          return prev.map((r) => {
            if (r.id === id) {
              return {
                ...r,
                adminNote: text,
                status: 'rejected'
              }
            }
            
            return r;
          })
        })
      }
    } catch (error: any) {
      toast.error(error?.message)
    } finally {
      setSelectedTransactionId(null)
    }
  };

  const handleApproveTransaction = async (id: number, text?: string | null) => {
    try {
      const eventId = rows.find((r) => r.id === id)?.event?.id;
      if (eventId) {
        await approveTransaction({ transactionId: id, eventId, adminNote: text })
        
        setRows((prev) => {
          return prev.map((r) => {
            if (r.id === id) {
              return {
                ...r,
                adminNote: text,
                status: 'withdrawn'
              }
            }

            return r;
          })
        })
      }
    } catch (error: any) {
      toast.error(error?.message)
    } finally {
      setSelectedTransactionId(null)
    }
  };
  //#endregion

  //#region Handlers

  function onLoadMoreClick () {
    organizerTransactionListDataLazyFetch({
      organizerId,
      type,
      skip: rows.length,
      take: TAKE_COUNT,
    })
      .then((response: any) => {
        const result = rows.concat(response.data.list)

        if (rows.length + TAKE_COUNT > 80) {
          result.splice(0, TAKE_COUNT)
          toast.success(translate('hideRows'))
        }

        setRows(result)
        setCount(response.data.count)
      })
      .catch((errors: any) => console.error(errors))
  }
  
  const downloadTransactionReceipt = async ({ eventId, transactionId }: { eventId: number; transactionId: number }) => {
    try {
      const response = await dispatch(eventApi.endpoints.getTransactionReceipt.initiate({ eventId, transactionId }))

      download(
        response.data,
        `transaction_${transactionId}`,
        'application/pdf'
      )

    } catch (error) {
    }
  };

  const handleMoveToInProgress = async (transactionId: number) => {
    try {
      const eventId = rows.find((r) => r.id === transactionId)?.event?.id;

      await dispatch(eventApi.endpoints.progressTransaction.initiate({ eventId, transactionId }))

      setRows((prev) => prev.map((i) => {
        if (i.id === transactionId) {
          return {
            ...i,
            inProgress: true,
          }
        }

        return i;
      }))
    } catch (error) {
    }
  };

  const handleCopyEventName = async (name: string, id: number) => {
    await navigator.clipboard.writeText(` [${id}] ${name}`);
  }

  //#endregion

  //#region UseEffect`s
  useEffect(() => {
    if (isOrganizersSuccess) {
      setRows(organizerTransactionListData.list)

      setCount(organizerTransactionListData.count)
    }
  }, [organizerTransactionListData, isOrganizersSuccess, setCount])

  useEffect(() => {
    const transactionNameInfoColumn = {
      field: 'transactionNameInfo',
      flex: 1,
      filtrable: false,
      sortable: true,
      headerName: translate('gridEventTransactionListTransactionNameInfo'),
      renderCell: (params: any) => {
        return (
          <Box className={classes.EventTransactionInfoCell}>
            <Typography>
              <span>{translate('gridEventTransactionListTransactionName')}:</span><br />{params.row.name}
            </Typography>
            {params.row.note ? (
              <Typography>
                <span>{translate('gridEventTransactionListTransactionDescription')}:</span><br />{params.row.note}
              </Typography>
            ) : null}
            {params.row.adminNote ? (
              <Typography>
                <span>{translate('gridEventTransactionListTransactionAdminDescription')}:</span><br />{params.row.adminNote}
              </Typography>
            ) : null}
          </Box>
        )
      }
    }

    const eventInfoColumn = {
      field: 'eventInfo',
      flex: 1,
      filtrable: false,
      sortable: true,
      headerName: translate('gridEventTransactionListEventInfo'),
      renderCell: (params: any) => {
        return (
          <Box className={classes.EventTransactionInfoCell}>
            <Typography>
              <b>[{params.row.event.id}] {params.row.event.name}</b>
              <IconButton color='warning' size='small' onClick={() => handleCopyEventName(params.row.event.name, params.row.event.id)}><ContentCopyIcon/></IconButton>
            </Typography>
            <Typography>
              <span>{translate('gridEventTransactionListEventStartDate')}:</span> {format(new Date(params.row.event.startDate), 'dd/MM/yyyy HH:mm')}
            </Typography>
            <Typography>
              <span>{translate('gridEventTransactionListEventEndDate')}:</span> {format(new Date(params.row.event.endDate), 'dd/MM/yyyy HH:mm')}
            </Typography>
            <Box my={1.5} width={1}>
              <Button
                size='small'
                color='warning'
                fullWidth
                onClick={() => showEventInfo(params.row.event.id)}
              >
                {translate('gridEventTransactionListEventShowInfo')}
              </Button>
            </Box>
          </Box>
        )
      }
    }

    const transactionInfoColumn = {
      field: 'transactionInfo',
      flex: 1,
      filtrable: false,
      sortable: true,
      headerName: translate('gridEventTransactionListTransactionInfo'),
      renderCell: (params: any) => {
        return (
          <Box className={classes.EventTransactionInfoCell}>
            {params.row.isLastTransaction ? (
              <Box display='flex' gap={1}>
                <Typography className={classes.EarlyWithdrawal}>
                  <span>{translate('gridEventTransactionListItemLastTransaction')}</span>
                </Typography>
                <TaskAltIcon sx={{ color: '#fc7f03' }} />
              </Box>
            ) : null}
            {params.row.isFastWithdrawal ? (
              <Typography className={classes.EarlyWithdrawal}>
                  <span>🚨🚨🚨{translate('gridEventTransactionListItemFastWithdrawal')}</span>
                </Typography>
              ) : null}
            {params.row.isAdminWithdrawal ? (
              <Typography className={classes.EarlyWithdrawal}>
                  <span>💰💰💰 {translate('gridEventTransactionListItemAdminWithdrawal')}</span>
                </Typography>
              ) : null}
            {params.row.isOverdraft ? (
              <Typography className={classes.EarlyWithdrawal}>
                  <span>➖❓🛠️ {translate('gridEventTransactionListItemOverdraft')}</span>
                </Typography>
              ) : null}
            {params.row.isEarlyWithdrawal ? (
              <Typography className={classes.EarlyWithdrawal}>
                  <span>{translate('gridEventTransactionListItemEarlyWithdrawal')}</span>
                </Typography>
              ) : null}
            <Typography>
              <span>{translate('gridEventTransactionListItemTotal')}:</span>{' '}
              {params.row.totalAmount}
            </Typography>
            <Typography>
              <span>{translate('gridEventTransactionListItemAmount')}:</span>{' '}
              {params.row.withdrawalAmount}
            </Typography>
            {params.row.feeAmount > 0 ? (
              <Typography>
                <span>{translate('gridEventTransactionListItemFee')}:</span>{' '}
                {params.row.feeAmount}
              </Typography>
            ) : null}
            {params.row.isAdminWithdrawal && params.row.adminWithdrawalFeeAmount && params.row.adminWithdrawalFeeAmount > 0 ? (
              <Typography>
                <span>{translate('gridEventTransactionListItemAdminFee')}:</span>{' '}
                {params.row.adminWithdrawalFeeAmount}
              </Typography>
            ) : null}
            {params.row.isFastWithdrawal && params.row.fastWithdrawalFeeAmount && params.row.fastWithdrawalFeeAmount > 0 ? (
              <Typography>
                <span>{translate('gridEventTransactionListItemFastFee')}:</span>{' '}
                {params.row.fastWithdrawalFeeAmount}
              </Typography>
            ) : null}
            {params.row.isEarlyWithdrawal && params.row.earlyWithdrawalFeeAmount && params.row.earlyWithdrawalFeeAmount > 0 ? (
              <Typography>
                <span>{translate('gridEventTransactionListItemEarlyFee')}:</span>{' '}
                {params.row.earlyWithdrawalFeeAmount}
              </Typography>
            ) : null}
          </Box>
        )
      }
    }

    const billAccountInfoColumn = {
      field: 'billAccountInfo',
      flex: 1,
      filtrable: false,
      sortable: true,
      headerName: translate('gridEventTransactionListBillAccountInfo'),
      renderCell: (params: any) => {
        if (!params.row.billAccount) {
          return null;
        }

        return (
          <Box className={classes.EventTransactionInfoCell}>
            <Typography>
              <span>{translate('gridEventTransactionListBillAccountName')}:</span>{' '}
              {params.row.billAccount.name}
            </Typography>
            {params.row.billAccount.description ? (
              <Typography>
                <span>{translate('gridEventTransactionListBillAccountDescription')}:</span>{' '}
                {params.row.billAccount.description}
              </Typography>
            ) : null}
            <Typography>
              <span>{translate('gridEventTransactionListBillAccountHolderName')}:</span>{' '}
              {params.row.billAccount.holderFullName}
            </Typography>
            <Typography>
              <span>
                {translate('gridEventTransactionListBillAccountBankName')}
              :</span>{' '}
              {params.row.billAccount.bankName}
            </Typography>
            <Typography>
              <span>
                {translate('gridEventTransactionListBillAccountBankNumber')}
              :</span>{' '}
              {params.row.billAccount.bankNumber}
            </Typography>
            <Typography>
              <span>
                {translate('gridEventTransactionListBillAccountBankAccountNumber')}
              :</span>{' '}
              {params.row.billAccount.bankAccountNumber}
            </Typography>
            {params.row.billAccount.idCode ? (
              <Typography>
                <span>
                  {translate('gridEventTransactionListBillAccountIdCode')}
                :</span>{' '}
                {params.row.billAccount.idCode}
              </Typography>
            ) : null}
          </Box>
        )
      }
    }

    const getStatusColor = (status: string) => {
      if (status === 'withdrawn') {
        return '#3ce23c'
      }

      if (status === 'pending') {
        return '#56ccf2'
      }

      if (status === 'rejected') {
        return '#FF2828'
      }

      if (status === 'cancelled') {
        return '#FF5353'
      }

      return ''
    }; 

    const getStatusLabel = (status: string) => {
      if (status === 'withdrawn') {
        return translate('gridEventTransactionListStatusWithdrawn')
      }

      if (status === 'pending') {
        return translate('gridEventTransactionListStatusPending')
      }

      if (status === 'rejected') {
        return translate('gridEventTransactionListStatusRejected')
      }

      if (status === 'cancelled') {
        return translate('gridEventTransactionListStatusCancelled')
      }

      return ''
    }; 

    const transactionDatesColumn = {
      field: 'transactionDates',
      flex: 1,
      filtrable: false,
      sortable: true,
      headerName: translate('gridEventTransactionListTransactionDates'),
      renderCell: (params: any) => {
        return (
          <Box className={classes.EventTransactionInfoCell}>
            <Typography>
              <span>{translate('gridEventTransactionListRequestDate')}:</span>{' '}
              <br />
              {params.row.createDate
                ? format(new Date(params.row.createDate), 'dd/MM/yyyy HH:mm')
                : '-'}
            </Typography>
            <Typography>
              <span>{translate('gridEventTransactionListLastChangeDate')}:</span>{' '}
              <br />
              {params.row.statusDate
                ? format(new Date(params.row.statusDate), 'dd/MM/yyyy HH:mm')
                : '-'}
            </Typography>
            <Typography>
              <span>{translate('gridEventTransactionListStatus')}:</span>{' '}
              <span style={{ color: getStatusColor(params.row.status), fontWeight: 500 }}>
                {getStatusLabel(params.row.status)}
              </span>
            </Typography>
          </Box>
        )
      }
    }

    let selectedColumns = [
      transactionNameInfoColumn,
      eventInfoColumn,
      transactionInfoColumn,
      billAccountInfoColumn,
      transactionDatesColumn
    ]

    selectedColumns.push({
      field: 'actions',
      flex: 1,
      filtrable: false,
      sortable: true,
      headerName: translate('gridEventTransactionListActions'),
      renderCell: (params: any) => {
        return (
          <Box className={classes.EventTransactionInfoCell}>
            {params.row.status === 'pending' ? (
              <Box display='flex' flexDirection='column' gap={1}>
                {params.row?.inProgress ? (
                  <Typography sx={{ color: '#56ccf2 '}} align='center'>
                    {translate('gridEventTransactionListInProgress')}
                  </Typography>
                ) : (
                  <Button
                    color='warning'
                    variant='outlined'
                    onClick={() => handleMoveToInProgress(params.row.id)}
                  >
                    {translate('gridEventTransactionListMoveToInProgressTransaction')}
                  </Button>
                )}
                <Button
                  color='warning'
                  variant='outlined'
                  onClick={() => handleOpenApproveTransactionModal(params.row.id)}
                >
                  {translate('gridEventTransactionListApproveTransaction')}
                </Button>
                <Button
                  color='warning'
                  variant='outlined'
                  onClick={() => handleOpenRejectTransactionModal(params.row.id)}
                >
                  {translate('gridEventTransactionListDeclineTransaction')}
                </Button>
              </Box>
            ) : null}
            {params.row.status === 'withdrawn' ? (
              <Box display='flex' flexDirection='column' gap={1}>
                <Button
                  color='warning'
                  variant='outlined'
                  onClick={() => downloadTransactionReceipt({ transactionId: params.row.id, eventId: params.row.event.id })}
                  >
                  {translate('gridEventTransactionListDownloadTransactionReceiptPdf')}
                </Button>
                {params.row.feePdfUrl && (
                  <Button
                    color='warning'
                    variant='outlined'
                    onClick={() => window?.open?.(params.row.feePdfUrl)?.focus()}
                    >
                    {translate('gridEventTransactionListDownloadTransactionFeePdf')}
                  </Button>
                )}
              </Box>
            ) : null}
          </Box>
        )
      }
    } as any)

    setColumns(selectedColumns.filter((c) => !!c))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows])

  useEffect(() => {
    organizerTransactionListDataRefetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, organizerId, transactionId])
  //#endregion

  if (isOrganizersFetching)
    return (
      <Box
        sx={{
          height: '400px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <CircularProgress color='warning' />
      </Box>
    )

  return (
    <>
      {selectedTransactionId && (
        <TransactionActionModal 
          transactionId={selectedTransactionId} 
          onSubmit={modalMode === 'approve' ? handleApproveTransaction : handleRejectTransaction}
          label={translate(modalMode === 'approve' ? 'approveTransaction': 'declineTransaction')}
          buttonText={translate(modalMode === 'approve' ? 'approveTransaction': 'declineTransaction')}
        />
      )}
      <Toaster position='bottom-right' toastOptions={{ duration: 4000 }} />
      <div
        style={{
          top: '0',
          width: '100%',
        }}
      >
        <DataGrid
          sx={{
            backgroundColor: '#ede9e4',
            borderRadius: '0px',
            border: 'none',

            '& .MuiDataGrid-virtualScroller': {
              marginTop: '0 !important'
            },
            '& .MuiDataGrid-main': {
              overflow: 'visible'
            },
            '.MuiDataGrid-columnHeaders': {
              backgroundColor: '#fc7f03',
              position: 'sticky',
              zIndex: 2,
              borderRadius: '0px',
              border: '0px'
            },
            '.MuiDataGrid-columnHeaderTitleContainer': {
              color: '#fff',
              fontSize: '15px',
              padding: '10px',
              display: 'flex',
              alignContent: 'center',
              justifyContent: 'center'
            },
            '.MuiDataGrid-cell': {
              border: '1px solid #fc7f03',
              padding: '5px',
              display: 'flex',
              alignContent: 'center',
              justifyContent: 'center'
            },
            '.MuiDataGrid-footerContainer': {
              border: '1px solid #fc7f03'
            },
            '.MuiDataGrid-footerContainer p': {
              margin: '0px'
            },
            '.MuiDataGrid-columnHeaderTitle': {
              overflow: 'visible',
              lineHeight: '1.43rem',
              whiteSpace: 'normal'
            }
          }}
          pageSize={rows.length}
          rows={rows}
          columns={columns}
          getRowHeight={() => 'auto'}
          disableSelectionOnClick
          autoHeight
          density='comfortable'
          headerHeight={70}
          hideFooter
        />
        <Button
          variant='contained'
          color='warning'
          style={{
            borderRadius: '0px',
            boxShadow: 'none'
          }}
          disabled={!(rows.length < count)}
          fullWidth
          onClick={() => onLoadMoreClick()}
          size='large'
        >
          {translate('customDataGridLoadMore')}
        </Button>
      </div>
    </>
  )
}

export default OrganizerTransactionDataGrid
